import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { ConversationDto, ConversationPrefsDto, Seq } from "../contract/chat.events";
import { compareSeq } from "../lib/seq";

/** Telegram / WhatsApp allow a few pinned chats; we keep it at three. */
export const MAX_PINNED = 3;

/**
 * Per-user list preferences: pinned (ordered, ≤ 3), archived, and «deleted»
 * chats (hidden up to the last seq at the moment of deletion — a new message
 * brings the chat back with only what came after).
 *
 * With chat contract v3 on the server these come in the conversation DTO and
 * are synced across devices (`hydrate`); until then they live on the device.
 * `uploaded` — what was set on this device before v3 has been sent to the
 * server once (v3/sync.ts), so switching v3 on doesn't lose it.
 */
interface PrefsState {
  ownerId: number | null;
  pinned: string[];
  archived: string[];
  cleared: Record<string, Seq>;
  uploaded: boolean;
  /** false → pin limit reached. */
  setPinned: (id: string, on: boolean) => boolean;
  setArchived: (id: string, on: boolean) => void;
  markCleared: (id: string, upToSeq: Seq) => void;
  /** Server truth (v3 DTO fields / `conversation:prefs` event). */
  hydrate: (c: Pick<ConversationPrefsDto, "pinnedAt" | "archivedAt" | "clearedUpToSeq"> & { id: string }) => void;
  markUploaded: () => void;
  claim: (userId: number) => void;
  clear: () => void;
}

const without = (list: string[], id: string) => list.filter((x) => x !== id);

export const useConversationPrefs = create<PrefsState>()(
  persist(
    (set, get) => ({
      ownerId: null,
      pinned: [],
      archived: [],
      cleared: {},
      uploaded: false,
      setPinned: (id, on) => {
        const { pinned } = get();
        if (on && !pinned.includes(id) && pinned.length >= MAX_PINNED) return false;
        // Pinning moves the chat out of the archive, like Telegram.
        set((s) => ({
          pinned: on ? [id, ...without(s.pinned, id)] : without(s.pinned, id),
          archived: on ? without(s.archived, id) : s.archived,
        }));
        return true;
      },
      setArchived: (id, on) =>
        set((s) => ({
          archived: on ? [id, ...without(s.archived, id)] : without(s.archived, id),
          pinned: on ? without(s.pinned, id) : s.pinned,
        })),
      markCleared: (id, upToSeq) =>
        set((s) => ({
          cleared: { ...s.cleared, [id]: upToSeq },
          pinned: without(s.pinned, id),
          archived: without(s.archived, id),
        })),
      hydrate: (c) =>
        set((s) => {
          let { pinned, archived } = s;
          const cleared = { ...s.cleared };
          if (c.pinnedAt !== undefined) pinned = c.pinnedAt ? (pinned.includes(c.id) ? pinned : [...pinned, c.id]) : without(pinned, c.id);
          if (c.archivedAt !== undefined) archived = c.archivedAt ? (archived.includes(c.id) ? archived : [c.id, ...archived]) : without(archived, c.id);
          // «Удалено до seq» only grows, like on the server (GREATEST): a DTO
          // computed before the delete landed (e.g. `conversation:updated`
          // for the read sent right before it) must not bring the chat back.
          const server = c.clearedUpToSeq;
          const local = cleared[c.id];
          if (server && (!local || compareSeq(server, local) > 0)) cleared[c.id] = server;
          const same =
            pinned === s.pinned && archived === s.archived && cleared[c.id] === s.cleared[c.id];
          return same ? s : { pinned, archived, cleared };
        }),
      markUploaded: () => set({ uploaded: true }),
      claim: (userId) =>
        set((s) =>
          s.ownerId === userId
            ? s
            : s.ownerId === null
              ? { ...s, ownerId: userId }
              : { ownerId: userId, pinned: [], archived: [], cleared: {}, uploaded: false },
        ),
      clear: () => set({ ownerId: null, pinned: [], archived: [], cleared: {}, uploaded: false }),
    }),
    {
      name: "turaq-chat-prefs",
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ ownerId: s.ownerId, pinned: s.pinned, archived: s.archived, cleared: s.cleared, uploaded: s.uploaded }),
    },
  ),
);

/** Deleted by me and nothing new since. */
export function isClearedOut(c: ConversationDto, cleared: Record<string, Seq>): boolean {
  // The server's mark counts even when this device's store was wiped (logout, another browser).
  const local = cleared[c.id];
  const server = c.clearedUpToSeq ?? null;
  const upTo = local && server ? (compareSeq(local, server) >= 0 ? local : server) : (local ?? server);
  if (!upTo) return false;
  return !c.lastMessage || compareSeq(c.lastMessage.seq, upTo) <= 0;
}

export const useIsPinned = (id: string) => useConversationPrefs((s) => s.pinned.includes(id));
export const useIsArchived = (id: string) => useConversationPrefs((s) => s.archived.includes(id));
export const useClearedSeq = (id: string) => useConversationPrefs((s) => s.cleared[id] ?? null);
